import React, { useState, useEffect } from "react";
import { Download, X, Smartphone, Bell, BellRing, Sparkles } from "lucide-react";
import { useI18n } from "../../i18n/I18nContext.js";
import {
  isPushNotificationSupported,
  getPushSubscriptionStatus,
  subscribeToPushNotifications,
} from "../../services/pushNotificationService.js";

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{
    outcome: "accepted" | "dismissed";
    platform: string;
  }>;
  prompt(): Promise<void>;
}

declare global {
  interface WindowEventMap {
    beforeinstallprompt: BeforeInstallPromptEvent;
  }
}

/**
 * Custom Hook for PWA Installability state and triggering
 */
export function usePwaInstall() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstallable, setIsInstallable] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    // Check if already in standalone mode
    if (
      typeof window !== "undefined" &&
      (window.matchMedia("(display-mode: standalone)").matches ||
        (window.navigator as any).standalone === true)
    ) {
      setIsInstalled(true);
      return;
    }

    const handleBeforeInstallPrompt = (e: BeforeInstallPromptEvent) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setIsInstallable(true);
    };

    const handleAppInstalled = () => {
      setIsInstalled(true);
      setIsInstallable(false);
      setDeferredPrompt(null);
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    window.addEventListener("appinstalled", handleAppInstalled);

    return () => {
      window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
      window.removeEventListener("appinstalled", handleAppInstalled);
    };
  }, []);

  const triggerInstall = async (): Promise<"accepted" | "dismissed" | "unavailable"> => {
    if (!deferredPrompt) return "unavailable";
    try {
      await deferredPrompt.prompt();
      const choice = await deferredPrompt.userChoice;
      if (choice.outcome === "accepted") {
        setIsInstalled(true);
        setIsInstallable(false);
      }
      setDeferredPrompt(null);
      return choice.outcome;
    } catch {
      return "unavailable";
    }
  };

  return { isInstallable, isInstalled, triggerInstall };
}

export interface PwaInstallBannerProps {
  className?: string;
  showPushPrompt?: boolean;
}

export const PwaInstallBanner: React.FC<PwaInstallBannerProps> = ({
  className = "",
  showPushPrompt = true,
}) => {
  const { t } = useI18n();
  const { isInstallable, isInstalled, triggerInstall } = usePwaInstall();
  const [dismissed, setDismissed] = useState<boolean>(() => {
    if (typeof localStorage !== "undefined") {
      return localStorage.getItem("hp_pwa_banner_dismissed") === "true";
    }
    return false;
  });

  const [pushStatus, setPushStatus] = useState<{
    supported: boolean;
    permission: NotificationPermission;
    isSubscribed: boolean;
  }>({
    supported: false,
    permission: "default",
    isSubscribed: false,
  });

  const [isSubscribingPush, setIsSubscribingPush] = useState(false);

  useEffect(() => {
    getPushSubscriptionStatus().then(setPushStatus);
  }, []);

  const handleDismiss = () => {
    setDismissed(true);
    try {
      localStorage.setItem("hp_pwa_banner_dismissed", "true");
    } catch {}
  };

  const handleInstallClick = async () => {
    const outcome = await triggerInstall();
    if (outcome === "accepted") {
      setDismissed(true);
    }
  };

  const handleEnablePush = async () => {
    setIsSubscribingPush(true);
    try {
      const res = await subscribeToPushNotifications();
      if (res.success) {
        setPushStatus({
          supported: true,
          permission: "granted",
          isSubscribed: true,
        });
      }
    } finally {
      setIsSubscribingPush(false);
    }
  };

  // If already installed or dismissed and no push opt-in pending, do not render banner
  if (dismissed || isInstalled || (!isInstallable && pushStatus.isSubscribed)) {
    return null;
  }

  // If not installable and push is not supported, hide
  if (!isInstallable && !pushStatus.supported) {
    return null;
  }

  const pwaText = t.pwa || {
    installApp: "Install HP-Helix App",
    installDesc: "Experience ultra low-latency playback, offline downloads, and native app performance directly on your device.",
    installButton: "Install App",
    dismiss: "Maybe later",
    enablePush: "Enable Notifications",
  };

  return (
    <aside
      aria-label="PWA Installation and Notifications"
      className={`relative overflow-hidden bg-gradient-to-r from-emerald-950/80 via-zinc-900 to-zinc-950 border border-emerald-500/30 rounded-2xl p-4 sm:p-5 shadow-2xl backdrop-blur-md animate-in fade-in slide-in-from-top-4 duration-300 ${className}`}
    >
      {/* Background Glow */}
      <div className="absolute -top-12 -right-12 w-36 h-36 bg-emerald-500/10 rounded-full blur-2xl pointer-events-none" />

      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 relative z-10">
        {/* Left: Icon & Description */}
        <div className="flex items-start gap-3.5">
          <div className="w-10 h-10 rounded-xl bg-emerald-500/20 border border-emerald-500/40 flex items-center justify-center text-emerald-400 shrink-0 shadow-lg shadow-emerald-500/10">
            <Smartphone className="w-5 h-5" />
          </div>
          <div className="space-y-0.5">
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-bold text-white tracking-tight">
                {pwaText.installApp}
              </h3>
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-mono font-semibold bg-emerald-500/15 text-emerald-400 border border-emerald-500/30">
                <Sparkles className="w-2.5 h-2.5" />
                PWA
              </span>
            </div>
            <p className="text-xs text-zinc-300 max-w-xl leading-relaxed">
              {pwaText.installDesc}
            </p>
          </div>
        </div>

        {/* Right: Actions */}
        <div className="flex items-center gap-2 w-full sm:w-auto shrink-0 justify-end">
          {/* Push Notifications Opt-In (if supported and not granted) */}
          {showPushPrompt &&
            pushStatus.supported &&
            !pushStatus.isSubscribed &&
            pushStatus.permission !== "denied" && (
              <button
                type="button"
                onClick={handleEnablePush}
                disabled={isSubscribingPush}
                className="px-3 py-2 rounded-xl bg-zinc-800 hover:bg-zinc-700 text-zinc-200 border border-zinc-700 text-xs font-semibold transition-all flex items-center gap-1.5 cursor-pointer shadow-md"
                title="Enable Push Notifications"
              >
                <BellRing className="w-3.5 h-3.5 text-emerald-400" />
                <span className="hidden md:inline">{pwaText.enablePush}</span>
              </button>
            )}

          {/* Install Button */}
          {isInstallable && (
            <button
              type="button"
              onClick={handleInstallClick}
              className="px-4 py-2 rounded-xl bg-emerald-500 hover:bg-emerald-400 active:scale-95 text-zinc-950 text-xs font-bold transition-all shadow-lg shadow-emerald-500/20 flex items-center gap-1.5 cursor-pointer"
            >
              <Download className="w-3.5 h-3.5" />
              <span>{pwaText.installButton}</span>
            </button>
          )}

          {/* Dismiss Button */}
          <button
            type="button"
            onClick={handleDismiss}
            aria-label="Dismiss banner"
            className="p-2 rounded-xl bg-zinc-900/80 hover:bg-zinc-800 text-zinc-400 hover:text-white transition-colors cursor-pointer"
            title={pwaText.dismiss}
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </aside>
  );
};
